import { WorkfrontTargetLevel } from './workfront-intake.enums.js'
import { WORKFRONT_TARGETS } from './workfront-targets.js'

/**
 * The work order field each Workfront target lands in, keyed by the target's path below its
 * level (`child.campaign` → `campaignCode`); every `tuple.*` target lands in `tuple`. A target
 * missing here is not a field a person edits on the order: only the sync writes it (cost status,
 * adaptation, readiness), or it lands outside the order (the PM rule, the external tasks).
 */
const WORK_ORDER_FIELD_OF_TARGET = {
	title: 'title',
	dueAt: 'dueAt',
	parentRef: 'parentWorkOrderId',
	assetTypeCode: 'assetTypeCode',
	complexityLevel: 'complexityLevel',
	campaign: 'campaignCode',
	targetVeevaCode: 'veevaCode',
	originalCodes: 'originalCodes',
	expectedPublishingDate: 'expectedPublishingDate',
	budget: 'budget',
	plannedCost: 'plannedCost',
	finalCost: 'finalCost',
} as const

/** A work order field the Workfront sync writes, named as the work order update names it. */
export type WorkfrontOwnedField = (typeof WORK_ORDER_FIELD_OF_TARGET)[keyof typeof WORK_ORDER_FIELD_OF_TARGET] | 'tuple'

/**
 * Fields the sync owns only once they hold a value: a child whose Workfront record has no size,
 * asset type or campaign keeps the one set in ATLAS, so a person may fill any of them while it is empty.
 */
export const WORKFRONT_KEPT_FIELDS = [
	'assetTypeCode',
	'complexityLevel',
	'campaignCode',
] as const satisfies readonly WorkfrontOwnedField[]

/** A field the sync owns only once it holds a value. */
export type WorkfrontKeptField = (typeof WORKFRONT_KEPT_FIELDS)[number]

const ownedAt = (level: WorkfrontTargetLevel): ReadonlySet<WorkfrontOwnedField> =>
	new Set<WorkfrontOwnedField>([
		...WORKFRONT_TARGETS.filter((target) => target.level === level).flatMap((target): WorkfrontOwnedField[] => {
			const field = target.path.slice(target.path.indexOf('.') + 1)
			if (field.startsWith('tuple.')) return ['tuple']
			const owned = WORK_ORDER_FIELD_OF_TARGET[field as keyof typeof WORK_ORDER_FIELD_OF_TARGET]
			return owned ? [owned] : []
		}),
		// The parent places an order at its level: a brief is top level, an asset sits under the
		// brief it came with. Changing it would make the order read as the other level.
		'parentWorkOrderId',
	])

const OWNED_AT_LEVEL = {
	[WorkfrontTargetLevel.PARENT]: ownedAt(WorkfrontTargetLevel.PARENT),
	[WorkfrontTargetLevel.CHILD]: ownedAt(WorkfrontTargetLevel.CHILD),
}

/**
 * The work order fields the Workfront sync writes at `level`. On an order from Workfront a
 * person may not change them: the next sync would overwrite the change. The whole tuple is
 * owned, because the sync replaces it whole and drops any axis ATLAS added.
 */
export const workfrontOwnedFieldsOf = (
	level: WorkfrontTargetLevel.PARENT | WorkfrontTargetLevel.CHILD,
): ReadonlySet<WorkfrontOwnedField> => OWNED_AT_LEVEL[level]

/** The level of an order from Workfront: an asset (child) is only ever created under its brief. */
export const workfrontLevelOf = (order: {
	parentWorkOrderId?: string | null
}): WorkfrontTargetLevel.PARENT | WorkfrontTargetLevel.CHILD =>
	order.parentWorkOrderId ? WorkfrontTargetLevel.CHILD : WorkfrontTargetLevel.PARENT
